import { useEffect, useRef, useState } from "react"
import { useRouter } from "next/navigation"
import { Meeting } from "@/schema"
import { Button } from "@ui/shad"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@ui/shad/dropdown-menu"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@ui/tab-pages"
import {
  AudioLines,
  Bot,
  Clock,
  Ellipsis,
  EyeOff,
  FileText,
  FolderPlus,
  Lock,
  LockKeyhole,
  MessageSquare,
  Replace,
  Trash2,
  TriangleAlert,
  Users,
  Video,
} from "lucide-react"
import { toast } from "sonner"

import { isMeetingPromptInProgress } from "@/lib/prompt-status"
import { useCreateRecordingTranscription, useDeleteMeeting } from "@/hooks/api/meetings"
import { useBookNotetakerPolicy } from "@/hooks/api/notetaker-policies"
import { useApiContext } from "@/hooks/api/use-api-context"
import { usePartnerAccess } from "@/hooks/use-partner-access"
import { CommentSection } from "@/components/comments"
import { ConfirmDialog } from "@/components/dialogs"
import { MeetingAuditLogLink } from "@/components/meetings/meeting-audit-log-link"
import { retentionModeOf } from "@/components/notetaker-policy/policy-resolution"
import { PartnerFeatureDialogGate, PartnerFeatureGate } from "@/components/partners/partner-feature-gate"
import { useCreateProjectDialog } from "@/components/projects/create-project-dialog"
import { PromptButtons } from "@/components/prompts/button-container"

import FindAndReplaceDialog from "../find-and-replace-dialog"
import MeetingPrivacyDialog from "../meeting-privacy-dialog"
import NotesAiSection from "../notes-ai-section"
import ShareMeetingDialog from "../share-meeting-dialog"
import MeetingConsentCard from "./meeting-consent-card"
import MeetingDownloadDropdown from "./meeting-download-dropdown"
import MeetingHeader from "./meeting-header"
import MeetingSpeakersList from "./meeting-speakers-list"
import MeetingTasksList from "./meeting-tasks-list"
import MultipleSpeakerReprocessingDialog from "./multiple-speaker-reprocessing-dialog"
import ReprocessRecordingDialog from "./reprocess-recording-dialog"
import TranscriptList from "./transcript-list"
import VideoPlayer from "./video-player"

const HIDDEN_TRANSCRIPT_MESSAGE =
  "Transcripts are hidden by your firm's notetaker policy. The AI summary is still available."

interface MeetingReviewViewProps {
  meeting?: Meeting
  isLoading: boolean
  initialSeekMs?: number
  initialTab?: "tasks" | "comments"
}

function MeetingReviewViewContent({ meeting, isLoading, initialSeekMs, initialTab }: MeetingReviewViewProps) {
  const { enabled: fullProductAccess } = usePartnerAccess("full_product_access")
  const meetingId = meeting?.id
  const [currentTime, setCurrentTime] = useState(0)
  const [collapsedVideo, setCollapsedVideo] = useState(false)
  const [showFindReplace, setShowFindReplace] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [showTranscriptionConfirm, setShowTranscriptionConfirm] = useState(false)
  const [showMultipleSpeakerDialog, setShowMultipleSpeakerDialog] = useState(false)
  const [showPrivacy, setShowPrivacy] = useState(false)
  const [editorRevision, setEditorRevision] = useState(0)
  const videoRef = useRef<HTMLVideoElement | HTMLAudioElement>(null)
  const transcriptContainerRef = useRef<HTMLDivElement>(null)
  const router = useRouter()
  const getContext = useApiContext()
  const deleteMeeting = useDeleteMeeting()
  const createRecordingTranscription = useCreateRecordingTranscription()
  const { data: notetakerPolicy } = useBookNotetakerPolicy(meeting?.book_id ?? "")
  const { open: openCreateProject, dialog: projectCreationDialog } = useCreateProjectDialog()

  function handleTimeUpdate(event: Event) {
    const mediaElement = event.target as HTMLVideoElement | HTMLAudioElement
    setCurrentTime(mediaElement.currentTime * 1000)

    if (mediaElement.currentTime < 0.5 && transcriptContainerRef.current) {
      transcriptContainerRef.current.scrollTo({ top: 0, behavior: "smooth" })
    }
  }

  function handleSeekTo(time: number) {
    if (videoRef.current) {
      videoRef.current.currentTime = time / 1000
      videoRef.current.play()
    }
  }

  // VideoPlayer mounts async after tokens resolve; poll for the element,
  // then wait for metadata before seeking.
  useEffect(() => {
    if (!fullProductAccess || initialSeekMs == null || !meetingId) return
    let cancelled = false
    let frameId = 0

    const seekWhenReady = () => {
      if (cancelled) return
      const el = videoRef.current
      if (!el) {
        frameId = requestAnimationFrame(seekWhenReady)
        return
      }
      const doSeek = () => {
        if (cancelled) return
        el.currentTime = initialSeekMs / 1000
        el.play().catch(() => {})
      }
      if (el.readyState >= 1) doSeek()
      else el.addEventListener("loadedmetadata", doSeek, { once: true })
    }
    seekWhenReady()

    return () => {
      cancelled = true
      cancelAnimationFrame(frameId)
    }
  }, [fullProductAccess, initialSeekMs, meetingId])

  function handleCollapseVideo() {
    setCollapsedVideo(!collapsedVideo)
  }

  async function handleDeleteMeeting() {
    if (!meeting) return
    await deleteMeeting.mutateAsync(meeting.id)
    toast.success("Meeting deleted")
    setShowDeleteConfirm(false)
    const context = await getContext()
    if (context?.bookId) {
      router.push(`/${context.bookId}/meetings`)
    }
  }

  function handleAddProject() {
    if (!meeting) return
    const households = meeting.households ?? []
    openCreateProject({
      bookId: meeting.book_id,
      householdId: households.length === 1 ? households[0].id : undefined,
    })
  }

  async function handleCreateRecordingTranscription(additionalInstructions?: string) {
    if (!meeting || !fullProductAccess) return

    await createRecordingTranscription.mutateAsync({ meetingId: meeting.id, additionalInstructions })
    setShowTranscriptionConfirm(false)
    toast.success("Transcribing recording with AI")
  }

  function getRetentionMessage(notetaker?: unknown): string | null {
    if (!notetaker || typeof notetaker !== "object") return null

    const retentionMode =
      "retentionMode" in notetaker
        ? notetaker.retentionMode
        : "retention_mode" in notetaker
          ? notetaker.retention_mode
          : undefined
    const retentionDays =
      "retentionDays" in notetaker
        ? notetaker.retentionDays
        : "retention_days" in notetaker
          ? notetaker.retention_days
          : undefined
    const purgeAfterAt =
      "purgeAfterAt" in notetaker
        ? notetaker.purgeAfterAt
        : "purge_after_at" in notetaker
          ? notetaker.purge_after_at
          : undefined

    if (!retentionMode || retentionMode === "keep_forever") {
      return null
    }

    if (retentionMode === "discard_immediately") {
      return "Video and transcript will be deleted after meeting notes are created"
    }

    if (retentionMode === "expire_after_days" && purgeAfterAt && typeof purgeAfterAt === "string") {
      const purgeDate = new Date(purgeAfterAt)
      const now = new Date()
      const daysUntilPurge = Math.ceil((purgeDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))

      if (daysUntilPurge <= 0) {
        return "Video and transcript will be deleted soon"
      } else if (daysUntilPurge === 1) {
        return "Video and transcript will be deleted in 1 day"
      } else {
        return `Video and transcript will be deleted in ${daysUntilPurge} days`
      }
    }

    if (retentionMode === "expire_after_days" && retentionDays) {
      return `Video and transcript will be deleted ${retentionDays} days after recording`
    }

    return null
  }

  // Check if transcript should show "not available" message due to discard_immediately setting
  function isTranscriptUnavailableDueToRetention(): boolean {
    // Always check book's notetaker settings for retention mode
    if (retentionModeOf(notetakerPolicy) === "discard_immediately") {
      return true
    }
    return false
  }

  const transcriptUnavailable = isTranscriptUnavailableDueToRetention()
  const transcriptHidden = Boolean(meeting?.transcript_hidden)
  const hasMuxVideo = Boolean(meeting?.mux_video)
  const hasRecordingMuxVideo = Boolean(meeting?.primary_recording?.mux_video)
  const hasRecordingMedia = Boolean(meeting?.primary_recording?.media?.url)
  const isAudioRecording =
    meeting?.primary_recording?.media?.media_type === "audio" && !hasMuxVideo && !hasRecordingMuxVideo
  const effectiveIsVideoCollapsed = isAudioRecording ? false : collapsedVideo
  const hasPlayableMedia =
    fullProductAccess &&
    !isLoading &&
    !transcriptUnavailable &&
    (hasMuxVideo || hasRecordingMuxVideo || hasRecordingMedia)
  const retentionMessage = getRetentionMessage(meeting?.notetaker)
  const isUserUploadedTranscript = Boolean(meeting?.user_uploaded_transcript)
  const isGeneratingTasks =
    isMeetingPromptInProgress(meeting, "Prompt::MeetingTasks") ||
    isMeetingPromptInProgress(meeting, "Prompt::MeetingNote")
  const isGeneratingMeetingTasks = isMeetingPromptInProgress(meeting, "Prompt::MeetingTasks")
  const reprocessingAlert = meeting?.recording_reprocessing_failed && !transcriptHidden && (
    <div
      role="alert"
      className="border-yellow-border bg-yellow-background text-yellow-foreground m-4 flex items-center gap-2 rounded-md border p-3 text-sm"
    >
      <TriangleAlert className="text-yellow-accent size-4 shrink-0" />
      <p className="flex-1">Recording reprocessing failed. Your original transcript is unchanged.</p>
      {fullProductAccess && meeting.recording_retranscription_available && (
        <Button size="sm" variant="outline" onClick={() => setShowTranscriptionConfirm(true)}>
          Try again
        </Button>
      )}
    </div>
  )

  if (!hasPlayableMedia && !isLoading) {
    return (
      <div className="flex h-full min-h-[calc(100vh-48px)]">
        {projectCreationDialog}
        <div className="w-[60%] border-r">
          <div className="flex items-start justify-between border-b px-4 pt-3 pb-3">
            <MeetingHeader meeting={meeting} isLoading={isLoading} />
            {meeting && (
              <div className="flex items-center gap-1.5">
                <PartnerFeatureGate feature="notetaker">
                  <ShareMeetingDialog
                    meetingId={meeting.id}
                    meetingTitle={meeting.title}
                    meetingStartTime={meeting.start_time}
                    participants={meeting.participants}
                    internalParticipants={meeting.internal_participants}
                  />
                </PartnerFeatureGate>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button size="iconSm" variant="outline">
                      <Ellipsis />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <MeetingAuditLogLink meeting={meeting} menuItem />
                    <DropdownMenuItem onClick={() => setShowFindReplace(true)}>
                      <Replace />
                      Find & replace
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleAddProject()}>
                      <FolderPlus />
                      Create project
                    </DropdownMenuItem>
                    {meeting.recording_retranscription_available && !meeting.transcript_purged && (
                      <DropdownMenuItem onClick={() => setShowTranscriptionConfirm(true)}>
                        <AudioLines />
                        Reprocess with AI
                      </DropdownMenuItem>
                    )}
                    {meeting.multiple_speaker_retranscription_available && !meeting.transcript_purged && (
                      <DropdownMenuItem onClick={() => setShowMultipleSpeakerDialog(true)}>
                        <Users />
                        Reprocess speakers
                      </DropdownMenuItem>
                    )}
                    {meeting.can_manage_privacy && (
                      <DropdownMenuItem onClick={() => setShowPrivacy(true)}>
                        <LockKeyhole />
                        {meeting.is_private ? "Manage privacy" : "Make private"}
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => setShowDeleteConfirm(true)}>
                      <Trash2 />
                      Delete meeting
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
          </div>
          {meeting && (
            <PartnerFeatureGate feature="notetaker">
              <MeetingConsentCard meeting={meeting} />
            </PartnerFeatureGate>
          )}
          {reprocessingAlert}
          <NotesAiSection meeting={meeting} isLoading={isLoading} editorRevision={editorRevision} />
        </div>
        <div className="flex h-[calc(100vh-48px)] min-h-0 w-[40%] flex-col">
          <Tabs defaultValue={initialTab ?? "tasks"} className="flex min-h-0 flex-1 flex-col">
            <div className="flex shrink-0 items-center justify-between border-b pr-4">
              <div className="[&>div]:border-b-0">
                <TabsList>
                  <TabsTrigger value="tasks" icon={<Bot className="text-icon" />}>
                    Tasks
                  </TabsTrigger>
                  <TabsTrigger value="transcript" icon={<FileText />}>
                    Transcript
                  </TabsTrigger>
                  <TabsTrigger value="comments" icon={<MessageSquare />}>
                    Comments
                  </TabsTrigger>
                  {!isUserUploadedTranscript && !transcriptHidden && (
                    <TabsTrigger value="speakers" icon={<Users />}>
                      Speakers
                    </TabsTrigger>
                  )}
                </TabsList>
              </div>
              <div>
                <PartnerFeatureGate feature="notetaker">
                  <MeetingDownloadDropdown
                    meeting={meeting}
                    hideRecordingDownloads={transcriptUnavailable}
                    hideTranscriptDownload={transcriptUnavailable || transcriptHidden}
                  />
                </PartnerFeatureGate>
              </div>
            </div>

            <TabsContent value="transcript" className="min-h-0 flex-1" disableMaxHeight>
              {transcriptHidden ? (
                <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
                  <Lock className="text-muted-foreground size-8" />
                  <p className="text-muted-foreground text-sm">{HIDDEN_TRANSCRIPT_MESSAGE}</p>
                </div>
              ) : transcriptUnavailable ? (
                <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
                  <Lock className="text-muted-foreground size-8" />
                  <p className="text-muted-foreground text-sm">
                    Transcript not available due to your retention settings
                  </p>
                </div>
              ) : (
                <PartnerFeatureGate feature="notetaker">
                  <TranscriptList
                    meeting={meeting}
                    handleSeekTo={handleSeekTo}
                    currentTime={currentTime}
                    isLoading={isLoading}
                    isVideoCollapsed={false}
                  />
                </PartnerFeatureGate>
              )}
            </TabsContent>
            <TabsContent value="tasks" className="min-h-0 flex-1" disableMaxHeight>
              <MeetingTasksList
                meeting={meeting}
                isVideoCollapsed={false}
                handleSeekTo={hasPlayableMedia ? handleSeekTo : undefined}
                isGeneratingTasks={isGeneratingTasks}
              />
            </TabsContent>
            <TabsContent value="comments" className="min-h-0 flex-1 overflow-y-auto p-4" disableMaxHeight>
              {meeting && <CommentSection resourceType="Meeting" resourceId={meeting.id} />}
            </TabsContent>
            {!isUserUploadedTranscript && !transcriptHidden && (
              <TabsContent value="speakers" className="min-h-0 flex-1" disableMaxHeight>
                <PartnerFeatureGate feature="notetaker">
                  <MeetingSpeakersList meeting={meeting} />
                </PartnerFeatureGate>
              </TabsContent>
            )}
          </Tabs>
        </div>
        {meeting && (
          <>
            <PartnerFeatureDialogGate
              feature="notetaker"
              title="Find and replace"
              open={showFindReplace}
              onOpenChange={setShowFindReplace}
            >
              <FindAndReplaceDialog
                meetingId={meeting.id}
                transcriptHidden={transcriptHidden}
                open={showFindReplace}
                onOpenChange={setShowFindReplace}
                onComplete={() => setEditorRevision((r) => r + 1)}
              />
            </PartnerFeatureDialogGate>
            <MeetingPrivacyDialog meetingId={meeting.id} open={showPrivacy} onOpenChange={setShowPrivacy} />
            <PartnerFeatureDialogGate
              feature="notetaker"
              title="Reprocess speakers"
              open={showMultipleSpeakerDialog}
              onOpenChange={setShowMultipleSpeakerDialog}
            >
              <MultipleSpeakerReprocessingDialog
                meeting={meeting}
                open={showMultipleSpeakerDialog}
                onOpenChange={setShowMultipleSpeakerDialog}
              />
            </PartnerFeatureDialogGate>
            <ReprocessRecordingDialog
              open={showTranscriptionConfirm}
              onOpenChange={setShowTranscriptionConfirm}
              onConfirm={handleCreateRecordingTranscription}
              isPending={createRecordingTranscription.isPending}
            />
            <ConfirmDialog
              open={showDeleteConfirm}
              onOpenChange={setShowDeleteConfirm}
              title={"Delete meeting?"}
              description={"This will permanently delete the meeting and all its data. This action cannot be undone."}
              dismiss={"explicit"}
              cancel={{ onClick: () => setShowDeleteConfirm(false) }}
              action={{ label: "Delete", onClick: handleDeleteMeeting, destructive: "destructive" === "destructive" }}
            />
          </>
        )}
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-[calc(100vh-48px)]">
      {projectCreationDialog}
      <div className="w-[60%] border-r">
        <div className="flex items-start justify-between border-b px-4 pt-3 pb-3">
          <MeetingHeader meeting={meeting} isLoading={isLoading} />
          {meeting && (
            <div className="flex items-center gap-1.5">
              <PartnerFeatureGate feature="notetaker">
                <ShareMeetingDialog
                  meetingId={meeting.id}
                  meetingTitle={meeting.title}
                  meetingStartTime={meeting.start_time}
                  participants={meeting.participants}
                  internalParticipants={meeting.internal_participants}
                />
              </PartnerFeatureGate>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="iconSm" variant="outline">
                    <Ellipsis />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <MeetingAuditLogLink meeting={meeting} menuItem />
                  <DropdownMenuItem onClick={() => setShowFindReplace(true)}>
                    <Replace />
                    Find & replace
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleAddProject()}>
                    <FolderPlus />
                    Create project
                  </DropdownMenuItem>
                  {meeting.recording_retranscription_available && !meeting.transcript_purged && (
                    <DropdownMenuItem onClick={() => setShowTranscriptionConfirm(true)}>
                      <AudioLines />
                      Reprocess with AI
                    </DropdownMenuItem>
                  )}
                  {meeting.multiple_speaker_retranscription_available && !meeting.transcript_purged && (
                    <DropdownMenuItem onClick={() => setShowMultipleSpeakerDialog(true)}>
                      <Users />
                      Reprocess speakers
                    </DropdownMenuItem>
                  )}
                  {meeting.can_manage_privacy && (
                    <DropdownMenuItem onClick={() => setShowPrivacy(true)}>
                      <LockKeyhole />
                      {meeting.is_private ? "Manage privacy" : "Make private"}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => setShowDeleteConfirm(true)}>
                    <Trash2 />
                    Delete meeting
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>
        {meeting && (
          <PartnerFeatureGate feature="notetaker">
            <MeetingConsentCard meeting={meeting} onSeek={hasPlayableMedia ? handleSeekTo : undefined} />
          </PartnerFeatureGate>
        )}
        {reprocessingAlert}
        <NotesAiSection meeting={meeting} isLoading={isLoading} editorRevision={editorRevision} />
      </div>
      <div className="flex h-[calc(100vh-48px)] min-h-0 w-[40%] flex-col">
        <PartnerFeatureGate feature="notetaker">
          <VideoPlayer
            muxVideo={meeting?.mux_video}
            recording={meeting?.primary_recording}
            meetingId={meeting?.id}
            isLoading={isLoading}
            onTimeUpdate={handleTimeUpdate}
            videoRef={videoRef}
            isVideoCollapsed={effectiveIsVideoCollapsed}
            downloadControl={
              <PartnerFeatureGate feature="notetaker">
                <MeetingDownloadDropdown
                  meeting={meeting}
                  hideRecordingDownloads={transcriptUnavailable}
                  hideTranscriptDownload={transcriptUnavailable || transcriptHidden}
                />
              </PartnerFeatureGate>
            }
            visibilityControl={
              !isAudioRecording ? (
                <Button onClick={handleCollapseVideo} variant="ghost" size="iconSm" className="text-icon">
                  {effectiveIsVideoCollapsed ? <Video /> : <EyeOff />}
                </Button>
              ) : undefined
            }
          />
        </PartnerFeatureGate>
        {retentionMessage && hasPlayableMedia && (
          <div className="text-muted-foreground flex shrink-0 items-center justify-center gap-1 px-4 pb-2 text-xs">
            <Clock className="h-3 w-3" />
            <span>{retentionMessage}</span>
          </div>
        )}
        <Tabs defaultValue={initialTab ?? "tasks"} className="flex min-h-0 flex-1 flex-col">
          <div className="shrink-0 border-t border-b">
            <div className="[&>div]:border-b-0">
              <TabsList>
                <TabsTrigger value="tasks" icon={<Bot className="text-icon" />}>
                  Tasks
                </TabsTrigger>
                <TabsTrigger value="transcript" icon={<FileText />}>
                  Transcript
                </TabsTrigger>
                <TabsTrigger value="comments" icon={<MessageSquare />}>
                  Comments
                </TabsTrigger>
                {!isUserUploadedTranscript && !transcriptHidden && (
                  <TabsTrigger value="speakers" icon={<Users />}>
                    Speakers
                  </TabsTrigger>
                )}
              </TabsList>
            </div>
          </div>

          <TabsContent value="transcript" className="min-h-0 flex-1" disableMaxHeight>
            {transcriptHidden ? (
              <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
                <Lock className="text-muted-foreground size-8" />
                <p className="text-muted-foreground text-sm">{HIDDEN_TRANSCRIPT_MESSAGE}</p>
              </div>
            ) : transcriptUnavailable ? (
              <div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
                <Lock className="text-muted-foreground size-8" />
                <p className="text-muted-foreground text-sm">Transcript not available due to your retention settings</p>
              </div>
            ) : (
              <PartnerFeatureGate feature="notetaker">
                <TranscriptList
                  meeting={meeting}
                  handleSeekTo={handleSeekTo}
                  currentTime={currentTime}
                  isLoading={isLoading}
                  isVideoCollapsed={effectiveIsVideoCollapsed}
                />
              </PartnerFeatureGate>
            )}
          </TabsContent>
          <TabsContent value="tasks" className="min-h-0 flex-1 overflow-visible!" disableMaxHeight>
            <>
              <div className="absolute -top-1 right-2 -translate-y-full">
                {meeting?.id && (
                  <PromptButtons
                    size="xs"
                    promptType="Prompt::MeetingTasks"
                    recordId={meeting.id}
                    bookId={meeting.book_id}
                    meetingType={meeting.meeting_type}
                    isPromptRunInProgress={isGeneratingMeetingTasks}
                  />
                )}
              </div>
              <MeetingTasksList
                meeting={meeting}
                isVideoCollapsed={effectiveIsVideoCollapsed}
                handleSeekTo={hasPlayableMedia ? handleSeekTo : undefined}
                isGeneratingTasks={isGeneratingTasks}
              />
            </>
          </TabsContent>
          <TabsContent value="comments" className="min-h-0 flex-1 overflow-y-auto p-4" disableMaxHeight>
            {meeting && <CommentSection resourceType="Meeting" resourceId={meeting.id} />}
          </TabsContent>
          {!isUserUploadedTranscript && !transcriptHidden && (
            <TabsContent value="speakers" className="min-h-0 flex-1" disableMaxHeight>
              <PartnerFeatureGate feature="notetaker">
                <MeetingSpeakersList meeting={meeting} />
              </PartnerFeatureGate>
            </TabsContent>
          )}
        </Tabs>
      </div>
      {meeting && (
        <>
          <PartnerFeatureDialogGate
            feature="notetaker"
            title="Find and replace"
            open={showFindReplace}
            onOpenChange={setShowFindReplace}
          >
            <FindAndReplaceDialog
              meetingId={meeting.id}
              transcriptHidden={transcriptHidden}
              open={showFindReplace}
              onOpenChange={setShowFindReplace}
              onComplete={() => setEditorRevision((r) => r + 1)}
            />
          </PartnerFeatureDialogGate>
          <MeetingPrivacyDialog meetingId={meeting.id} open={showPrivacy} onOpenChange={setShowPrivacy} />
          <PartnerFeatureDialogGate
            feature="notetaker"
            title="Reprocess speakers"
            open={showMultipleSpeakerDialog}
            onOpenChange={setShowMultipleSpeakerDialog}
          >
            <MultipleSpeakerReprocessingDialog
              meeting={meeting}
              open={showMultipleSpeakerDialog}
              onOpenChange={setShowMultipleSpeakerDialog}
            />
          </PartnerFeatureDialogGate>
          <ReprocessRecordingDialog
            open={showTranscriptionConfirm}
            onOpenChange={setShowTranscriptionConfirm}
            onConfirm={handleCreateRecordingTranscription}
            isPending={createRecordingTranscription.isPending}
          />
          <ConfirmDialog
            open={showDeleteConfirm}
            onOpenChange={setShowDeleteConfirm}
            title={"Delete meeting?"}
            description={"This will permanently delete the meeting and all its data. This action cannot be undone."}
            dismiss={"explicit"}
            cancel={{ onClick: () => setShowDeleteConfirm(false) }}
            action={{ label: "Delete", onClick: handleDeleteMeeting, destructive: "destructive" === "destructive" }}
          />
        </>
      )}
    </div>
  )
}

export default function MeetingReviewView(props: React.ComponentProps<typeof MeetingReviewViewContent>) {
  const { isLoading } = usePartnerAccess("full_product_access")
  if (isLoading) return null
  return <MeetingReviewViewContent {...props} />
}
